import React, { useState, useRef, useEffect } from 'react';
import { portfolioData } from '../data/portfolioData';
import { sound } from './SoundSystem';

export default function CyberTerminal({ onOpenCv }) {
  const [history, setHistory] = useState([
    { type: 'system', text: 'Système Cyber Terminal v3.8.0 [Initialisé avec succès]' },
    { type: 'system', text: 'Tapez "help" ou cliquez sur un bouton pour explorer le profil de Babacar Diagne.' }
  ]);
  const [inputVal, setInputVal] = useState('');
  const [isMatrixActive, setIsMatrixActive] = useState(false);
  const terminalEndRef = useRef(null);

  const scrollToBottom = () => {
    if (terminalEndRef.current) {
      terminalEndRef.current.scrollIntoView({ behavior: 'smooth' });
    }
  };

  useEffect(() => {
    scrollToBottom();
  }, [history]);

  const executeCommand = (cmdText) => {
    const raw = cmdText.trim();
    if (!raw) return;
    const cmd = raw.toLowerCase();

    sound.playClick();
    const newHistory = [...history, { type: 'user', text: `$ ${raw}` }];

    switch (cmd) {
      case 'help':
        newHistory.push({
          type: 'output',
          text: `Commandes disponibles :
  - help      : Affiche cette liste de commandes
  - bio       : Résumé de profil & vision
  - skills    : Liste des compétences clés & stack
  - projects  : Liste des réalisations & liens de production
  - cv        : Ouvre & télécharge le CV PDF Haute Définition
  - contact   : Coordonnées téléphoniques, email et WhatsApp
  - matrix    : Bascule l'effet Matrix Digital Rain
  - clear     : Efface l'historique du terminal`
        });
        break;

      case 'bio':
        newHistory.push({
          type: 'output',
          text: `[PROFIL] : Babacar Diagne — Ingénieur Logiciel Full Stack & Lead Tech
[STARTUP]: Co-fondateur de AppliCodeTech
[POSTE]  : Enseignant en Génie Informatique & Responsable CIAQ (Université Kocc Barma)
[MISSION]: Concevoir des architectures résilientes, former la relève et digitaliser les écosystèmes.`
        });
        break;

      case 'skills':
        newHistory.push({
          type: 'output',
          text: `[STACK MAÎTRISÉE] :
  • Backend  : Java (Core, OOP), Spring Boot, Spring Security, PHP / Symfony
  • Frontend : React.js, Angular, JavaScript ES6+, Bootstrap 5, Tailwind
  • Cloud    : AWS Cloud, VPS OVH Linux, Docker, Jenkins CI/CD, SonarQube
  • Database : MySQL, PostgreSQL, Modélisation UML / Merise
  • Outils   : Git / GitHub / Bitbucket, Jira, Postman, Moodle, WordPress`
        });
        break;

      case 'projects':
        newHistory.push({
          type: 'output',
          text: `[PROJETS EN PRODUCTION & R&D] :
  1. DPC (Direction Pêche Continentale) -> https://dpc.gouv.sn
  2. SGEP (ERP Université Kocc Barma)   -> https://vps-d463d072.vps.ovh.net
  3. NauticaSenegal 2.0                 -> Système de surveillance maritime
  4. SGST-PIED (Master UNCHK)           -> Tableau de bord Spring Boot / AWS
  5. SanarAgro                          -> https://sanaragro.sn`
        });
        break;

      case 'cv':
        newHistory.push({
          type: 'output',
          text: `[CV] Lancement de l'inspecteur CV exécutif 2026... Téléchargement prêt !`
        });
        if (onOpenCv) onOpenCv();
        break;

      case 'contact':
        newHistory.push({
          type: 'output',
          text: `[CONTACT DIRECT] :
  • Téléphone principal  : ${portfolioData.personal.phone}
  • Téléphone alternatif : ${portfolioData.personal.phoneAlternative}
  • Email officiel       : ${portfolioData.personal.email}
  • Localisation         : ${portfolioData.personal.location}
  • WhatsApp direct      : https://wa.me/221773490318`
        });
        break;

      case 'matrix':
        setIsMatrixActive(!isMatrixActive);
        newHistory.push({
          type: 'output',
          text: `[MATRIX] Mode pluie digitale ${!isMatrixActive ? 'ACTIVÉ' : 'DÉSACTIVÉ'}.`
        });
        break;

      case 'clear':
        setHistory([]);
        setInputVal('');
        return;

      case 'sudo':
        newHistory.push({
          type: 'output',
          text: `[SECURITY] babacar est déjà super-administrateur avec tous les privilèges.`
        });
        break;

      default:
        newHistory.push({
          type: 'error',
          text: `Commande inconnue: "${raw}". Tapez "help" pour voir les instructions.`
        });
    }

    setHistory(newHistory);
    setInputVal('');
  };

  const handleKeyDown = (e) => {
    if (e.key === 'Enter') {
      executeCommand(inputVal);
    }
  };

  const quickCommands = ['help', 'bio', 'skills', 'projects', 'cv', 'contact', 'clear'];

  return (
    <div className={`cyber-terminal-card ${isMatrixActive ? 'matrix-mode' : ''}`}>
      {/* Terminal Titlebar */}
      <div className="terminal-card-header">
        <div className="terminal-dots">
          <span className="dot red"></span>
          <span className="dot yellow"></span>
          <span className="dot green"></span>
        </div>
        <div className="terminal-title">
          <i className="fa-solid fa-terminal"></i>
          <span>bash — babacar@dev-station:~ (zsh)</span>
        </div>
        <div className="terminal-status-badge">
          <span className="status-blink"></span>
          LIVE 60FPS
        </div>
      </div>

      {/* Terminal Quick Action Buttons */}
      <div className="terminal-quick-actions">
        <span className="quick-label">Raccourcis:</span>
        {quickCommands.map((cmd) => (
          <button
            key={cmd}
            className="quick-chip"
            onClick={() => executeCommand(cmd)}
          >
            {cmd}
          </button>
        ))}
      </div>

      {/* Terminal Output Area */}
      <div className="terminal-body-scroll">
        {history.map((item, idx) => (
          <div key={idx} className={`term-line ${item.type}`}>
            <pre>{item.text}</pre>
          </div>
        ))}
        <div ref={terminalEndRef} />
      </div>

      {/* Terminal Prompt Input Bar */}
      <div className="terminal-input-bar">
        <span className="terminal-prompt-user">babacar@workstation</span>
        <span className="terminal-prompt-sym">:~$</span>
        <input
          type="text"
          className="term-input"
          value={inputVal}
          onChange={(e) => setInputVal(e.target.value)}
          onKeyDown={handleKeyDown}
          placeholder="Entrez une commande (ex: help, projects, cv, contact)..."
        />
        <button className="term-send-btn" onClick={() => executeCommand(inputVal)}>
          <i className="fa-solid fa-play"></i>
        </button>
      </div>
    </div>
  );
}
